import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePortAvailability } from '../../hooks'
import {
  DEFAULT_PORTS,
  type ExternalServiceCredentials,
  type InstallInput,
} from '../../types/velociraptor-settings'
import { DEPLOY_DOCKER, DEPLOY_EXTERNAL, DEPLOY_LOCAL_BINARY } from './constants'
import { parseApiClientYaml } from './yaml-helpers'

export interface InstallFormState {
  deploymentType: string
  setDeploymentType: (value: string) => void
  adminUsername: string
  setAdminUsername: (value: string) => void
  adminPassword: string
  setAdminPassword: (value: string) => void
  apiPort: number
  setApiPort: (value: number) => void
  guiPort: number
  setGuiPort: (value: number) => void
  frontendPort: number
  setFrontendPort: (value: number) => void
  binaryPath: string
  setBinaryPath: (value: string) => void
  datastorePath: string
  setDatastorePath: (value: string) => void
  yamlText: string
  yamlError: string | null
  credentialsFromEnv: boolean
  setCredentialsFromEnv: (value: boolean) => void
  parsedCredentials: ExternalServiceCredentials | null
  handleYamlChange: (val: string) => void
  portAvailability: ReturnType<typeof usePortAvailability>
  isExternal: boolean
  isLocalBinary: boolean
  isSubmitDisabled: boolean
  buildInstallInput: () => InstallInput
}

interface UseInstallFormStateOptions {
  installing: boolean
}

interface CredentialsState {
  adminUsername: string
  adminPassword: string
  setAdminUsername: (value: string) => void
  setAdminPassword: (value: string) => void
  binaryPath: string
  datastorePath: string
  setBinaryPath: (value: string) => void
  setDatastorePath: (value: string) => void
}

function useCredentialsState(): CredentialsState {
  const [adminUsername, setAdminUsername] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [binaryPath, setBinaryPath] = useState('')
  const [datastorePath, setDatastorePath] = useState('')
  return {
    adminUsername,
    adminPassword,
    setAdminUsername,
    setAdminPassword,
    binaryPath,
    datastorePath,
    setBinaryPath,
    setDatastorePath,
  }
}

interface PortsState {
  apiPort: number
  guiPort: number
  frontendPort: number
  setApiPort: (value: number) => void
  setGuiPort: (value: number) => void
  setFrontendPort: (value: number) => void
}

function usePortsState(): PortsState {
  const [apiPort, setApiPort] = useState<number>(DEFAULT_PORTS.api)
  const [guiPort, setGuiPort] = useState<number>(DEFAULT_PORTS.gui)
  const [frontendPort, setFrontendPort] = useState<number>(DEFAULT_PORTS.frontend)
  return { apiPort, guiPort, frontendPort, setApiPort, setGuiPort, setFrontendPort }
}

interface YamlState {
  yamlText: string
  yamlError: string | null
  credentialsFromEnv: boolean
  setCredentialsFromEnv: (value: boolean) => void
  parsedCredentials: ExternalServiceCredentials | null
  handleYamlChange: (val: string) => void
}

function useYamlState(): YamlState {
  const { t } = useTranslation('admin')
  const [yamlText, setYamlText] = useState('')
  const [yamlError, setYamlError] = useState<string | null>(null)
  const [credentialsFromEnv, setCredentialsFromEnv] = useState(false)
  const [parsedCredentials, setParsedCredentials] = useState<ExternalServiceCredentials | null>(
    null
  )
  const handleYamlChange = (val: string): void => {
    setYamlText(val)
    if (val.trim() === '') {
      setYamlError(null)
      setParsedCredentials(null)
      return
    }
    const result = parseApiClientYaml(val)
    if (result === null) {
      setYamlError(t('velociraptorSettings.install.yamlParseError'))
      setParsedCredentials(null)
    } else {
      setYamlError(null)
      setParsedCredentials(result)
    }
  }
  return {
    yamlText,
    yamlError,
    credentialsFromEnv,
    setCredentialsFromEnv,
    parsedCredentials,
    handleYamlChange,
  }
}

interface BuildInputArgs {
  deploymentType: string
  ports: PortsState
  creds: CredentialsState
  yaml: YamlState
  isExternal: boolean
  isLocalBinary: boolean
}

function buildInstallInputFor(args: BuildInputArgs): InstallInput {
  const input: InstallInput = {
    deploymentType: args.deploymentType,
    apiPort: args.ports.apiPort,
    guiPort: args.ports.guiPort,
    frontendPort: args.ports.frontendPort,
  }
  if (args.isLocalBinary) {
    input.binaryPath = args.creds.binaryPath
    input.datastorePath = args.creds.datastorePath
    input.adminUsername = args.creds.adminUsername
    input.adminPassword = args.creds.adminPassword
  } else if (args.isExternal) {
    input.credentialsFromEnv = args.yaml.credentialsFromEnv
    if (args.yaml.parsedCredentials !== null) {
      input.apiCredentials = args.yaml.parsedCredentials
    }
  } else {
    input.adminUsername = args.creds.adminUsername
    input.adminPassword = args.creds.adminPassword
  }
  return input
}

function isSubmitDisabledFor(args: {
  installing: boolean
  isExternal: boolean
  isExternalReady: boolean
  adminUsername: string
  adminPassword: string
}): boolean {
  return (
    args.installing ||
    (!args.isExternal && (!args.adminUsername || !args.adminPassword)) ||
    (args.isExternal && !args.isExternalReady)
  )
}

export function useInstallFormState({ installing }: UseInstallFormStateOptions): InstallFormState {
  const [deploymentType, setDeploymentType] = useState<string>(DEPLOY_DOCKER)
  const creds = useCredentialsState()
  const ports = usePortsState()
  const yaml = useYamlState()
  const portAvailability = usePortAvailability()

  const isExternal = deploymentType === DEPLOY_EXTERNAL
  const isLocalBinary = deploymentType === DEPLOY_LOCAL_BINARY
  const isExternalReady = yaml.credentialsFromEnv || yaml.parsedCredentials !== null
  const isSubmitDisabled = isSubmitDisabledFor({
    installing,
    isExternal,
    isExternalReady,
    adminUsername: creds.adminUsername,
    adminPassword: creds.adminPassword,
  })

  const buildInstallInput = (): InstallInput =>
    buildInstallInputFor({ deploymentType, ports, creds, yaml, isExternal, isLocalBinary })

  return {
    deploymentType,
    setDeploymentType,
    adminUsername: creds.adminUsername,
    setAdminUsername: creds.setAdminUsername,
    adminPassword: creds.adminPassword,
    setAdminPassword: creds.setAdminPassword,
    apiPort: ports.apiPort,
    setApiPort: ports.setApiPort,
    guiPort: ports.guiPort,
    setGuiPort: ports.setGuiPort,
    frontendPort: ports.frontendPort,
    setFrontendPort: ports.setFrontendPort,
    binaryPath: creds.binaryPath,
    setBinaryPath: creds.setBinaryPath,
    datastorePath: creds.datastorePath,
    setDatastorePath: creds.setDatastorePath,
    yamlText: yaml.yamlText,
    yamlError: yaml.yamlError,
    credentialsFromEnv: yaml.credentialsFromEnv,
    setCredentialsFromEnv: yaml.setCredentialsFromEnv,
    parsedCredentials: yaml.parsedCredentials,
    handleYamlChange: yaml.handleYamlChange,
    portAvailability,
    isExternal,
    isLocalBinary,
    isSubmitDisabled,
    buildInstallInput,
  }
}
